// CSP Cost Optimization MVP: presentation-only matching over the customer's
// existing licensing projection. This module intentionally makes no pricing,
// overlap, entitlement, or usage conclusion.
export const CSP_RULES = Object.freeze([
  Object.freeze({
    id: "suite-copilot",
    priority: 10,
    requiredSkus: Object.freeze(["SPE_E5", "Microsoft_365_Copilot"]),
    title: "Review your Microsoft 365 and Copilot mix",
    explanation: "Your inventory includes both products. A review can explore whether a different licensing arrangement better fits your plans and budget.",
    qualification: null,
  }),
  Object.freeze({
    id: "suite-power-bi",
    priority: 20,
    requiredSkus: Object.freeze(["SPE_E5", "POWER_BI_PRO"]),
    title: "Check standalone products alongside your suite",
    explanation: "Both products appear in your inventory. It may be worth reviewing how they are assigned and whether there is an opportunity to consolidate.",
    qualification: "Different people may need these licenses. Their presence alone does not mean you are paying twice.",
  }),
]);

const isSafeNonnegativeInteger = (value) =>
  Number.isSafeInteger(value) && value >= 0;

const isMatchingProduct = (product) =>
  product &&
  product.capability_status === "Enabled" &&
  Number.isSafeInteger(product.enabled_units) && product.enabled_units > 0 &&
  isSafeNonnegativeInteger(product.assigned_units) &&
  isSafeNonnegativeInteger(product.available_units);

export const isEligibleLicensingSnapshot = (snapshot) =>
  !!snapshot &&
  snapshot.available === true &&
  snapshot.collection_incomplete === false &&
  snapshot.staleness === "fresh" &&
  Array.isArray(snapshot.products);

/**
 * Returns an eligibility state, unique review topics, and exact participating
 * SKU strings. A malformed row invalidates that SKU even if another row with
 * the same SKU is valid, so the UI never chooses a convenient duplicate.
 */
export const evaluateCspOpportunities = (snapshot) => {
  if (!isEligibleLicensingSnapshot(snapshot)) {
    return { eligibility: "ineligible", topics: [], participatingSkus: new Set() };
  }

  const skuValidity = new Map();
  for (const product of snapshot.products) {
    const sku = product?.sku_part_number;
    if (typeof sku !== "string") continue;
    const valid = isMatchingProduct(product);
    const previous = skuValidity.get(sku);
    skuValidity.set(sku, previous === false ? false : valid);
  }

  const topics = CSP_RULES
    .filter((rule) => rule.requiredSkus.every((sku) => skuValidity.get(sku) === true))
    .sort((a, b) => a.priority - b.priority);
  const participatingSkus = new Set(topics.flatMap((topic) => topic.requiredSkus));
  return { eligibility: "eligible", topics, participatingSkus };
};

export const cspOpportunityCountLabel = (count) =>
  `${count} ${count === 1 ? "area" : "areas"}`;
